import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Header, Title, CardList, Modal } from '../../components';

import { getCardSelector } from '../../redux/profile/cards/selectors';
import { fetchGetCards } from '../../redux/profile/cards/asyncActions';
import { cartSelector } from '../../redux/cart/selectors';
import { getProfileInfo } from '../../redux/profile/settings/getProfileInfo/selectors';
import { fetchProfileInfo } from '../../redux/profile/settings/getProfileInfo/asyncActions';
import { useAppDispatch } from '../../redux/store';
import store from '../../redux/store';

import styles from './Payment.module.scss';

import { ModalTexts } from '../../resources/locale';
import { appController } from '../../resources/constants';
import { differenceDate, getTotalPriceFromStorage } from '../../utils';
import { ITotalCartItems } from '../../redux/cart/types';
import { PaymentPropsType } from './types';

import { createPaymentLink, getPaymentInfo } from '@/redux/actions/payment.action';
import { compareItems } from '@/utils/payment/paymentItemsBuilder';
import { getArenasNames } from '@/utils/payment/getArenasNames';
import { handleSuccessPayment } from '@/utils/payment/paymentSuccessHandler';
import { getErrorCharacterization } from '@/utils/payment/paymentErrorHandler';
import { usePaymentPolling } from '@/hooks/usePaymentPolling';
import { getJustPaidFlag } from '@/utils/payment/justPaidFlag';

// Глобальный тип для виджета YooKassa
declare global {
  interface Window {
    YooMoneyCheckoutWidget?: any;
  }
}

// Загрузка скрипта YooKassa checkout widget
function loadYooKassaWidget(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.YooMoneyCheckoutWidget) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load YooKassa widget script'));
    document.head.appendChild(script);
  });
}

export const Payment: React.FC<PaymentPropsType> = ({ title }) => {
  const { cardId, cards } = useSelector(getCardSelector);
  const { totalCartItems } = useSelector(cartSelector);
  const { profileInfo } = useSelector(getProfileInfo);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const [isOpenModalAfk, setIsOpenModalAfk] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [methodSelected, setMethodSelected] = useState<boolean>(false);
  const [errorProcessed, setErrorProcessed] = useState<boolean>(false);
  const [successProcessed, setSuccessProcessed] = useState<boolean>(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [sendingDataTo1C, setSendingDataTo1C] = useState<boolean>(false);
  const [paymentCreationError, setPaymentCreationError] = useState<any>(null);
  const [paymentCardLoading, setPaymentCardLoading] = useState<boolean>(false);
  const [guid, setGuid] = useState<string>('');
  const [isProcessing3DSec, setIsProcessing3DSec] = useState<boolean>(false);
  const [widgetRendered, setWidgetRendered] = useState<boolean>(false);
  // URL платёжной формы Т-Кассы (Тинькофф). Пусто для ЮKassa.
  const [tinkoffUrl, setTinkoffUrl] = useState<string | null>(null);

  // Refs
  const handledRef = useRef<boolean>(false);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const widgetInstanceRef = useRef<any>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Сброс состояния при монтировании
  useEffect(() => {
    setErrorProcessed(false);
    setSuccessProcessed(false);
    setPaymentCreationError(null);
    setPaymentId(null);
    setIsProcessing3DSec(false);
    setWidgetRendered(false);
    handledRef.current = false;

    // Уничтожаем виджет при размонтировании
    return () => {
      if (widgetInstanceRef.current) {
        try {
          widgetInstanceRef.current.destroy();
        } catch (e) {
          // ignore
        }
        widgetInstanceRef.current = null;
      }
    };
  }, []);

  // Инициализация - получение totalPrice из storage
  useEffect(() => {
    const storedTotalPrice = getTotalPriceFromStorage();
    if (storedTotalPrice !== null && storedTotalPrice !== undefined) {
      setTotalPrice(storedTotalPrice);
    }
  }, []);

  // Обновление списка карт после возврата со страницы привязки
  useEffect(() => {
    const checkCardBindingReturn = () => {
      const paymentIdParam = searchParams.get('payment_id');
      const status = searchParams.get('status');

      if (paymentIdParam && status === 'succeeded' && !paymentId) {
        dispatch(fetchGetCards());

        if (window.opener) {
          window.close();
        }
      }
    };

    checkCardBindingReturn();
  }, [searchParams, dispatch, paymentId]);

  // Проверка истечения времени (AFK)
  useEffect(() => {
    if (getJustPaidFlag()) {
      return;
    }

    testTimeAfk(totalCartItems, appController.AFK_TIMER_MINUTES);

    const testTimeAfkTimer = setInterval(() => {
      testTimeAfk(totalCartItems, appController.AFK_TIMER_MINUTES);
    }, 5000);

    return () => {
      clearInterval(testTimeAfkTimer);
    };
  }, [totalCartItems]);

  const goToSuccess = React.useCallback(async () => {
    if (handledRef.current) {
      return;
    }

    handledRef.current = true;
    setSuccessProcessed(true);

    try {
      const racketData = getArenasNames(store.getState());
      const courtRent = totalCartItems.seats || {};
      const racketRent = totalCartItems.rackets || {};
      const productsInBasket = totalCartItems.products || {};
      const storageRent = totalCartItems.storage || {};

      const bitrixResult = compareItems(
        false,
        racketData,
        courtRent as any,
        racketRent as any,
        productsInBasket as any,
        profileInfo?.phone || '',
        storageRent as any
      );

      await handleSuccessPayment(
        guid,
        bitrixResult.items as any,
        bitrixResult.hasGruntReOrder,
        paymentId || '',
        dispatch,
        navigate,
        setSendingDataTo1C,
        setPaymentCardLoading,
        profileInfo?.phone || '',
        (error) => {
          console.error('Ошибка обработки успешной оплаты:', error);
          navigate('/payment-error', { state: error });
        }
      );
    } catch (error: any) {
      console.error('Ошибка обработки успешной оплаты:', error);
      navigate('/payment-error', { state: 'Ошибка обработки успешной оплаты' });
    }
  }, [guid, paymentId, dispatch, navigate, profileInfo?.phone, totalCartItems]);

  const errorPayment = React.useCallback(async (urlParam: string) => {
    if (errorProcessed) {
      return;
    }

    setErrorProcessed(true);

    const errorChar = await getErrorCharacterization(urlParam, paymentId || undefined, paymentCreationError);

    navigate('/payment-error', {
      state: {
        errorReason: errorChar.reason,
        errorType: errorChar.errorType,
        orderId: errorChar.orderId,
      },
    });
  }, [paymentId, paymentCreationError, navigate, errorProcessed]);

  // Polling статуса платежа
  usePaymentPolling({
    paymentId,
    successProcessed,
    errorProcessed,
    handledRef,
    goToSuccess,
    errorPayment,
  });

  const testTimeAfk = (cartItems: ITotalCartItems | any, minutes: number) => {
    for (let type in cartItems) {
      for (let item in cartItems[type]) {
        const timeStart =
          type === 'seats'
            ? new Date(cartItems[type][item].startTimeCourt)
            : new Date(cartItems[type][item].startTimeRacket);

        const timeNow = new Date();
        const difference = differenceDate(timeNow, timeStart);
        const time = minutes * 60_000;

        if (difference >= time) {
          setIsOpenModalAfk(true);
        }
      }
    }
  };

  const handleNavigateToCartIfAfk = () => {
    navigate('/cart');
  };

  // Инициализация виджета YooKassa
  const initWidget = async (confirmationToken: string) => {
    try {
      await loadYooKassaWidget();

      // Уничтожаем предыдущий инстанс если есть
      if (widgetInstanceRef.current) {
        try {
          widgetInstanceRef.current.destroy();
        } catch (e) {
          // ignore
        }
      }

      const checkout = new window.YooMoneyCheckoutWidget({
        confirmation_token: confirmationToken,
        return_url: `${window.location.origin}/payment-confirm`,
        error_callback: (error: any) => {
          console.error('YooKassa widget error:', error);
          errorPayment('widget_error');
        },
      });

      widgetInstanceRef.current = checkout;

      // Рендерим виджет в контейнер
      if (widgetContainerRef.current) {
        await checkout.render('payment-widget-container');
        setWidgetRendered(true);
      }
    } catch (error) {
      console.error('Ошибка инициализации виджета YooKassa:', error);
      errorPayment('widget_load_error');
    }
  };

  const setLink = async () => {
    try {
      setLoading(true);
      setPaymentCreationError(null);

      let currentProfileInfo = profileInfo;
      if (!currentProfileInfo?.phone) {
        await dispatch(fetchProfileInfo());
        const state = store.getState();
        if (state.getProfile?.profileInfo) {
          currentProfileInfo = state.getProfile.profileInfo;
        }
      }

      const phone = currentProfileInfo?.phone || '';
      const email = currentProfileInfo?.email || '';

      if (!phone) {
        throw new Error('Телефон пользователя не найден');
      }

      const racketData = getArenasNames(store.getState());

      const courtRent = totalCartItems.seats || {};
      const racketRent = totalCartItems.rackets || {};
      const productsInBasket = totalCartItems.products || {};
      const storageRent = totalCartItems.storage || {};

      const kassaResult = compareItems(
        true,
        racketData,
        courtRent as any,
        racketRent as any,
        productsInBasket as any,
        phone,
        storageRent as any
      );

      const bitrixResult = compareItems(
        false,
        racketData,
        courtRent as any,
        racketRent as any,
        productsInBasket as any,
        phone,
        storageRent as any
      );

      // Токен сохранённой карты: у ЮKassa это payment_method_id, у Т-Кассы —
      // RebillId привязанной карты. Пусто — платим новой картой в форме банка.
      const selectedCard = cardId !== 0 ? cards.find(card => card.id === cardId) : null;
      const cardToken = selectedCard?.token || null;

      const result = await createPaymentLink(
        (url) => setTinkoffUrl(url), // Т-Касса: URL платёжной формы
        kassaResult.totalPrice,
        phone,
        email,
        cardToken,
        (id) => setPaymentId(id),
        kassaResult.description,
        kassaResult.items as any,
        (guid) => setGuid(guid),
        bitrixResult.items as any,
        (token) => {
          // При получении confirmation_token инициализируем виджет
          initWidget(token);
        }
      );

      if (!result.success) {
        setPaymentCreationError(result.error);
        errorPayment('payment_creation_error');
        return;
      }

      // Для сохранённой карты без токена — запускаем polling
      if (result.hasSavedCard && !result.confirmationToken) {
        setIsProcessing3DSec(true);
      }
    } catch (error: any) {
      console.error('Ошибка создания платежа:', error);
      setPaymentCreationError(error);
      errorPayment('payment_creation_error');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPayment = async () => {
    // Способ выбирается всегда: сохранённая карта либо «другим способом»
    // (новая карта в форме банка).
    if (!methodSelected) {
      return;
    }

    await setLink();
  };

  const handleSelectCard = () => {
    setMethodSelected(true);
  };

  const isLoading = loading || sendingDataTo1C || paymentCardLoading || isProcessing3DSec;

  return (
    <>
      <Header title={title} />
      <main>
        <section className="container">
          <Title>{title}</Title>
          <div className={styles.paymentContainer}>
            {!widgetRendered && !tinkoffUrl && (
              <div className={styles.paymentSection}>
                <CardList onPage={'payment'} onClickFunc={handleSelectCard} />
              </div>
            )}

            {/* Контейнер для виджета YooKassa */}
            <div
              id="payment-widget-container"
              ref={widgetContainerRef}
              style={{ display: widgetRendered ? 'block' : 'none' }}
            />

            {/* Платёжная форма Т-Кассы (Тинькофф) — на странице через iframe */}
            {tinkoffUrl && (
              <div className={styles.tinkoffFrameWrap}>
                <iframe
                  title="Оплата Т-Касса"
                  src={tinkoffUrl}
                  className={styles.tinkoffFrame}
                  allow="payment *"
                />
                <p className={styles.tinkoffFallback}>
                  Форма не открылась?{' '}
                  <a href={tinkoffUrl} target="_blank" rel="noopener noreferrer">
                    Открыть оплату в новом окне
                  </a>
                </p>
              </div>
            )}

            {!widgetRendered && !tinkoffUrl && (
              <Button
                newStyles={styles.button}
                onClickFunc={handleStartPayment}
                disabled={isLoading || !methodSelected}
              >
                <span>
                  {isLoading
                    ? sendingDataTo1C
                      ? 'Отправка данных...'
                      : isProcessing3DSec
                      ? 'Обработка платежа...'
                      : 'Обработка...'
                    : 'Оплатить'}
                </span>
              </Button>
            )}
          </div>
        </section>
      </main>
      {isOpenModalAfk ? (
        <Modal
          title={ModalTexts.payment.afk.TITLE}
          subTitle={ModalTexts.payment.afk.SUB_TITLE}
          isOpen={isOpenModalAfk}
          setIsOpen={setIsOpenModalAfk}
          closeFunc={handleNavigateToCartIfAfk}
        >
          <p className={styles.modalText}>
            Некоторые товары из вашей корзины были удалены, чтобы быть доступными для других
            пользователей
          </p>
          <p className={styles.modalText}>Сейчас вы будете перенаправлены в корзину</p>
          <Button newStyles={styles.button} onClickFunc={handleNavigateToCartIfAfk}>
            Ок
          </Button>
        </Modal>
      ) : null}
    </>
  );
};
